import { motion } from "framer-motion";
import { X, Footprints, Car, Bike, Clock, Ruler, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const formatDistance = (m) => {
  if (m == null) return "—";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 2 : 1)} km`;
};

const formatDuration = (s) => {
  if (s == null) return "—";
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${h} h ${rem} min` : `${h} h`;
};

const MODES = [
  { key: "foot",    label: "Walk",   Icon: Footprints },
  { key: "driving", label: "Drive",  Icon: Car },
  { key: "cycling", label: "Cycle",  Icon: Bike },
];

export default function DirectionsModal({
  isOpen,
  onClose,
  restroom,
  route,
  loading,
  error,
  mode = "foot",
  onModeChange,
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-end sm:items-center justify-center p-3">
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
        data-testid="directions-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Directions to</p>
            <h3 className="font-semibold text-slate-900 truncate">{restroom?.name || "Restroom"}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close"
            data-testid="close-directions"
          >
            <X size={20} />
          </button>
        </div>

        {/* Mode picker */}
        <div className="flex gap-2 px-5 pt-4">
          {MODES.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => onModeChange?.(key)}
              data-testid={`mode-${key}`}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition ${
                mode === key
                  ? "bg-sky-600 text-white border-sky-600"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        {/* Summary */}
        <div className="px-5 py-4">
          {loading && (
            <div className="animate-pulse space-y-2">
              <div className="h-6 bg-slate-100 rounded w-1/3" />
              <div className="h-4 bg-slate-100 rounded w-2/3" />
            </div>
          )}
          {error && (
            <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">
              {error}
            </p>
          )}
          {!loading && !error && route && (
            <div className="flex items-center gap-4">
              <div>
                <div className="text-2xl font-bold text-slate-900 flex items-center gap-1.5">
                  <Clock size={20} className="text-sky-600" />
                  {formatDuration(route.duration_s)}
                </div>
                <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-1">
                  <Ruler size={14} />
                  {formatDistance(route.distance_m)}
                </div>
              </div>
              <div className="ml-auto text-xs text-slate-400 max-w-[45%] text-right">
                {route.steps?.length || 0} step{route.steps?.length === 1 ? "" : "s"}<br />
                <span className="text-slate-500">via OpenStreetMap</span>
              </div>
            </div>
          )}
        </div>

        {/* Steps list */}
        {!loading && !error && route?.steps?.length ? (
          <ScrollArea className="max-h-[45vh] px-5">
            <ol className="space-y-1 pb-4">
              {route.steps.map((step, i) => (
                <li
                  key={i}
                  className="flex gap-3 py-2 border-b border-slate-100 last:border-0"
                  data-testid={`step-${i}`}
                >
                  <div className="shrink-0 w-8 h-8 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center text-sm font-semibold">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800">{step.instruction}</p>
                    <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                      <span>{formatDistance(step.distance_m)}</span>
                      {step.duration_s > 0 && (
                        <>
                          <ArrowRight size={12} />
                          <span>{formatDuration(step.duration_s)}</span>
                        </>
                      )}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </ScrollArea>
        ) : null}

        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50">
          <Button
            onClick={onClose}
            variant="outline"
            className="w-full rounded-lg"
            data-testid="dismiss-directions"
          >
            Done
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
